'use client';

import { AlertTriangle, Info, OctagonAlert, X, type LucideIcon } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

type Variant = 'info' | 'warning' | 'error';

const variants: Record<
  Variant,
  { icon: LucideIcon; container: string; iconColor: string; dismiss: string }
> = {
  info: {
    icon: Info,
    container: 'border-blue-200 bg-blue-50 text-blue-900',
    iconColor: 'text-blue-500',
    dismiss: 'text-blue-500 hover:bg-blue-100 hover:text-blue-700',
  },
  warning: {
    icon: AlertTriangle,
    container: 'border-amber-300 bg-amber-50 text-amber-950',
    iconColor: 'text-amber-600',
    dismiss: 'text-amber-700 hover:bg-amber-200 hover:text-amber-900',
  },
  error: {
    icon: OctagonAlert,
    container: 'border-red-300 bg-red-50 text-red-950',
    iconColor: 'text-red-600',
    dismiss: 'text-red-600 hover:bg-red-100 hover:text-red-800',
  },
};

type IncidentBannerProps = {
  message: string;
  variant?: Variant;
  dismissLabel?: string;
  className?: string;
};

function IncidentBanner({
  message,
  variant = 'info',
  dismissLabel = 'Dismiss',
  className,
}: IncidentBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  const { icon: Icon, container, iconColor, dismiss } = variants[variant];

  if (dismissed) return null;

  return (
    <div
      data-slot="incident-banner"
      className={cn('flex items-center gap-3 border-b px-4 py-2 text-sm', container, className)}
    >
      <Icon className={cn('size-4 shrink-0', iconColor)} />
      <span className="flex-1">{message}</span>
      <button
        onClick={() => setDismissed(true)}
        className={cn('shrink-0 rounded-sm p-0.5 transition-colors', dismiss)}
        aria-label={dismissLabel}
      >
        <X className="size-4 cursor-pointer" />
      </button>
    </div>
  );
}

export { IncidentBanner };
export type { IncidentBannerProps };
